import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Stack, styled } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { IEmail } from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDialog,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { number, object, string } from 'yup';

const validationSchema = object().shape({
  fromEmail: string().required('From Email is required').email('Email is invalid'),
  host: string().required('Host is required'),
  port: number().required('Port number is required'),
  password: string()
    .min(4, 'Password must be at least 4 characters')
    .required('Password is required'),
});

export interface CustomMailDailogProps {
  openDialog: boolean;
  closeDialog(): void;
  title: string;
  onSaveHandler(args: IEmail): void;
  variant?: 'yahoo' | 'godaddy';
  onTestHandler(args: IEmail): void;
}
const StyledStack = styled(Stack)({
  marginTop: 10,
  padding: 10,
  backgroundColor: WsColors.TheBlues50,
  gap: 10,
});
const CustomMailDailog: FC<CustomMailDailogProps> = ({
  openDialog,
  closeDialog,
  title,
  onSaveHandler,
  variant,
  onTestHandler,
}) => {
  const [protectionValue, setProtectionValue] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const formOptions = {
    resolver: yupResolver(validationSchema),
  };
  const methods = useForm(formOptions);
  const { handleSubmit, setValue } = methods;
  const setFormData = (data: FieldValues) => {
    return {
      fromEmail: data.fromEmail,

      host: data.host,

      isDefault: isDefault,

      password: data.password,

      port: data.port.toString(),

      protection: protectionValue,

      fromName: '',

      userName: '',
    };
  };
  const onFormSubmitSave = (data: FieldValues) => onSaveHandler(setFormData(data));
  const onFormSubmitTest = (data: FieldValues) => onTestHandler(setFormData(data));
  const formPrefillData: IEmail = {
    fromEmail: '',
    fromName: '',
    host: '',
    isDefault: false,
    password: '',
    port: 0,
    protection: '',
    userName: '',
  };
  const setFormPrefillData = (host: string, port: number, protection: string) => {
    formPrefillData.isDefault = isDefault;
    formPrefillData.host = host;
    formPrefillData.port = port;
    formPrefillData.protection = protection;
  };
  if (variant === 'yahoo') setFormPrefillData('smtp.mail.yahoo.com', 465, 'SSL');
  if (variant === 'godaddy') setFormPrefillData('smtpout.secureserver.net', 465, 'SSL');
  useEffect(() => {
    if (variant === 'yahoo' || variant === 'godaddy') {
      setProtectionValue('SSl');
      setValue('host', formPrefillData.host);
      setValue('port', formPrefillData.port);
      setDisabled(true);
    }
  }, [formPrefillData.host, formPrefillData.port, setValue, variant]);
  const setSelectedIndexDefaultGroup = (selectedIndexValue: number) => {
    if (selectedIndexValue === 0) {
      setIsDefault(true);
    }
    if (selectedIndexValue === 1) {
      setIsDefault(false);
    }
  };
  const setSelectedIndexEncryptionGroup = (selectedIndexValue: number) => {
    if (selectedIndexValue === 0) {
      setProtectionValue('SSL');
    }
    if (selectedIndexValue === 1) {
      setProtectionValue('TLS');
    }
    if (selectedIndexValue === 2) {
      setProtectionValue('');
    }
  };
  return (
    <Box>
      <WsDialog size={'large'} openDialog={openDialog}>
        <Stack
          direction={'row'}
          bgcolor={WsColors.TertiaryShades50}
          justifyContent={'space-between'}
        >
          <Item>
            <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.SUB_TITLE}>
              {title}
            </WsTypography>
          </Item>
          <Item>
            <CloseIcon onClick={() => closeDialog()} sx={{ cursor: 'pointer' }}></CloseIcon>
          </Item>
        </Stack>
        <Box>
          <FormProvider {...methods}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={3} md={6} borderRadius={1.5}>
                  <Stack spacing={2}>
                    <ControlledTextInput
                      name="fromEmail"
                      labelText="Email*"
                      type="email"
                      fullWidth
                      id="fromEmail"
                      placeholder="Eg, johndoe@email.com"
                      variant="outlined"
                      required
                    />
                    <ControlledTextInput
                      name="password"
                      labelText="Password*"
                      type="password"
                      fullWidth
                      id="password"
                      placeholder="password"
                      variant="outlined"
                      required
                    />
                    <ControlledTextInput
                      name="port"
                      labelText="Port*"
                      type="number"
                      fullWidth
                      id="port"
                      disabled={disabled}
                      placeholder="465"
                      variant="outlined"
                      required
                    />
                  </Stack>
                </Grid>
                <Grid item xs={3} md={6} borderRadius={1.5}>
                  <Stack spacing={2}>
                    <ControlledTextInput
                      name="host"
                      labelText="SMTP Server *"
                      type="text"
                      fullWidth
                      id="host"
                      placeholder="smtpout.example.net"
                      variant="outlined"
                      required
                      disabled={disabled}
                    />
                    <WsButtonGroup
                      inputTitle={'Connection Security *'}
                      buttonsList={['SSL', 'TLS', 'None']}
                      selectedIndexValue={(selectedIndexValue) =>
                        setSelectedIndexEncryptionGroup(selectedIndexValue)
                      }
                    ></WsButtonGroup>
                    <WsButtonGroup
                      inputTitle={'Set as Default'}
                      buttonsList={['Yes', 'No']}
                      selectedIndexValue={(selectedIndexValue) =>
                        setSelectedIndexDefaultGroup(selectedIndexValue)
                      }
                    ></WsButtonGroup>
                  </Stack>
                </Grid>
              </Grid>
              <StyledStack direction={'row'} justifyContent={'flex-end'}>
                <WsButton onClick={() => closeDialog()} variant="text" size="large">
                  Cancel
                </WsButton>
                <WsButton
                  onClick={handleSubmit(onFormSubmitTest)}
                  variant="outlined"
                  size="large"
                  type="submit"
                >
                  Test Connection
                </WsButton>
                <WsButton
                  type="submit"
                  onClick={handleSubmit(onFormSubmitSave)}
                  variant="contained"
                  size="large"
                >
                  Save
                </WsButton>
              </StyledStack>
            </form>
          </FormProvider>
        </Box>
      </WsDialog>
    </Box>
  );
};
export default CustomMailDailog;
